/**
 * easing.ts — Pure easing curves and lifetime helpers for animation/effects.
 *
 * Renderer-free so it stays unit-testable. All easings map [0,1] → [0,1] and are
 * clamped, so callers can pass a raw elapsed/duration ratio without guarding.
 */

/** Clamp a raw ratio into [0,1]. */
export function progress01(elapsed: number, duration: number): number {
  if (duration <= 0) return 1;
  const t = elapsed / duration;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t);

/** Decelerating curve — fast out of the gate, gentle at the end. */
export function easeOutCubic(t: number): number {
  const k = clamp01(t);
  return 1 - Math.pow(1 - k, 3);
}

/** Gentler deceleration than cubic. */
export function easeOutQuad(t: number): number {
  const k = clamp01(t);
  return 1 - (1 - k) * (1 - k);
}

/** Accelerating curve — slow start, quick finish. */
export function easeInQuad(t: number): number {
  const k = clamp01(t);
  return k * k;
}

/**
 * A single 0 → 1 → 0 arc. Useful for hops and pops where something rises and
 * falls over one cycle. Peaks at exactly t = 0.5.
 */
export function arc01(t: number): number {
  const k = clamp01(t);
  return Math.sin(k * Math.PI);
}
